import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { itemEnvelope, zDecimal, zId, zInstant, zMinor } from './envelope';
import { apiRequest, apiVoid } from './http';
import { applyWrite, invalidateResources } from './invalidate';
import { useIdempotentMutation } from './mutations';
import {
  defineResource,
  useResourceItem,
  useResourceList,
  type ListParams,
  type ListQueryOptions,
} from './resource';

export const INVOICE_STATUSES = ['draft', 'open', 'partially_paid', 'paid', 'void', 'uncollectible'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const taxSnapshotSchema = z
  .object({
    tax_rate_id: z.string().nullish(),
    name: z.string().nullish(),
    jurisdiction: z.string().nullish(),
    percentage: zDecimal.nullish(),
    inclusive: z.boolean().nullish(),
    amount_minor: zMinor.nullish(),
    taxable_minor: zMinor.nullish(),
  })
  .loose();
export type TaxSnapshot = z.infer<typeof taxSnapshotSchema>;

/** BillerAPI `InvoiceResult::line`. */
export const invoiceLineSchema = z.object({
  id: zId,
  position: z.number().int(),
  source_type: z.string(),
  source_id: z.string().nullable(),
  subscription_item_id: z.string().nullable(),
  product_id: z.string().nullable(),
  price_id: z.string().nullable(),
  /** Set when the invoice belongs to a parent paying for this customer's service. */
  service_customer_id: z.string().nullable(),
  description: z.string(),
  quantity: zDecimal,
  unit_amount: zDecimal,
  currency: z.string(),
  service_period_start: zInstant.nullable(),
  service_period_end: zInstant.nullable(),
  proration: z.boolean(),
  discount_percent: zDecimal,
  tax_behavior: z.string().nullish(),
  tax_rate_ids: z.array(z.string()),
  subtotal_minor: zMinor,
  discount_minor: zMinor,
  tax_minor: zMinor,
  total_minor: zMinor,
  tax_snapshot: z.array(taxSnapshotSchema).nullish(),
  calculation_trace: z.unknown().nullish(),
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

/** BillerAPI `InvoiceResult`; `lines` is present on the detail endpoint. */
export const invoiceSchema = z.object({
  id: zId,
  number: z.string().nullable(),
  customer_id: zId,
  subscription_id: z.string().nullable(),
  /** One cycle invoice gathering several subscriptions (customer invoice grouping). */
  consolidated: z.boolean(),
  status: z.enum(INVOICE_STATUSES),
  is_past_due: z.boolean(),
  currency: z.string(),
  collection_method: z.string(),
  billing_reason: z.string(),
  payment_terms_days: z.number().int().nullish(),
  issue_date: z.string().nullable(),
  due_date: z.string().nullable(),
  period_start: zInstant.nullable(),
  period_end: zInstant.nullable(),
  subtotal_minor: zMinor,
  discount_minor: zMinor,
  tax_minor: zMinor,
  total_minor: zMinor,
  amount_paid_minor: zMinor,
  amount_credited_minor: zMinor,
  amount_written_off_minor: zMinor,
  amount_due_minor: zMinor,
  memo: z.string().nullable(),
  footer: z.string().nullable(),
  customer_snapshot: z.record(z.string(), z.unknown()).nullable(),
  /** The issuing legal entity; null when the tenant bills as itself. */
  legal_entity_id: z.string().nullable().default(null),
  /** The issuer frozen at finalization (BillerAPI `LegalEntityResolver::snapshot`). */
  issuer_snapshot: z.record(z.string(), z.unknown()).nullable().default(null),
  collection_policy_version_id: z.string().nullable(),
  finalized_at: zInstant.nullable(),
  paid_at: zInstant.nullable(),
  voided_at: zInstant.nullable(),
  marked_uncollectible_at: zInstant.nullable(),
  version: z.number().int(),
  created_at: zInstant.nullable(),
  updated_at: zInstant.nullable(),
  lines: z.array(invoiceLineSchema).optional(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

export const invoices = defineResource('invoices', '/invoices', invoiceSchema, {
  isComplete: (invoice) => invoice.lines !== undefined,
});

export interface InvoiceListParams extends ListParams {
  customer_id?: string | undefined;
  subscription_id?: string | undefined;
  status?: readonly InvoiceStatus[] | undefined;
  number?: string | undefined;
  past_due?: boolean | undefined;
}

export function useInvoices(params: InvoiceListParams = {}, options: ListQueryOptions = {}) {
  return useResourceList(invoices, params, options);
}

export function useInvoice(id: string | undefined) {
  return useResourceItem(invoices, id);
}

/** Display status: open/partially paid invoices past their due date read as "overdue". */
export function invoiceDisplayStatus(invoice: Pick<Invoice, 'status' | 'is_past_due'>): string {
  return invoice.is_past_due && (invoice.status === 'open' || invoice.status === 'partially_paid')
    ? 'overdue'
    : invoice.status;
}

export interface InvoiceLineInput {
  description: string;
  quantity?: string;
  /** Decimal string; may be negative for a discount line. Required without price_id. */
  unit_amount?: string | null;
  price_id?: string | null;
  discount_percent?: string;
  tax_rate_ids?: string[];
  tax_behavior?: 'exclusive' | 'inclusive';
  period_start?: string | null;
  period_end?: string | null;
}

export interface CreateInvoiceInput {
  customer_id: string;
  currency?: string;
  collection_method?: 'charge_automatically' | 'send_invoice';
  payment_terms_days?: number;
  memo?: string | null;
  footer?: string | null;
  include_pending_items?: boolean;
  auto_apply_credit?: boolean;
  lines?: InvoiceLineInput[];
}

const invoiceEnvelope = itemEnvelope(invoiceSchema);

export function useCreateInvoice() {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (input: CreateInvoiceInput, idempotencyKey) =>
      (await apiRequest(invoiceEnvelope, '/invoices', { method: 'POST', body: input, idempotencyKey })).data,
    onSuccess: (invoice) => {
      applyWrite(client, invoices, invoice);
      invalidateResources(client, billableItems);
    },
  });
}

export function useUpdateDraftInvoice(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      changes,
      version,
    }: {
      changes: Partial<
        Pick<
          CreateInvoiceInput,
          'memo' | 'footer' | 'payment_terms_days' | 'collection_method' | 'auto_apply_credit'
        >
      >;
      version?: number;
    }) =>
      (
        await apiRequest(invoiceEnvelope, `/invoices/${id}`, {
          method: 'PATCH',
          body: changes,
          ifMatch: version,
        })
      ).data,
    onSuccess: (invoice) => applyWrite(client, invoices, invoice),
  });
}

export function useAddInvoiceLines(id: string) {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async ({ lines, version }: { lines: InvoiceLineInput[]; version?: number }, idempotencyKey) =>
      (
        await apiRequest(invoiceEnvelope, `/invoices/${id}/lines`, {
          method: 'POST',
          body: { lines },
          idempotencyKey,
          ifMatch: version,
        })
      ).data,
    onSuccess: (invoice) => applyWrite(client, invoices, invoice),
  });
}

export function useRemoveInvoiceLine(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (lineId: string) =>
      (await apiRequest(invoiceEnvelope, `/invoices/${id}/lines/${lineId}`, { method: 'DELETE' })).data,
    onSuccess: (invoice) => applyWrite(client, invoices, invoice),
  });
}

export function useDeleteDraftInvoice(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiVoid(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      client.removeQueries({ queryKey: invoices.keys.detail(id) });
      void client.invalidateQueries({ queryKey: invoices.keys.lists() });
    },
  });
}

type InvoiceCommand = 'finalize' | 'void' | 'mark-uncollectible';

/** Finalize (assigns the number, posts to the ledger), void, or write off. */
export function useInvoiceCommand(id: string, command: InvoiceCommand) {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (input: { reason?: string | undefined } | void, idempotencyKey) =>
      (
        await apiRequest(invoiceEnvelope, `/invoices/${id}/${command}`, {
          method: 'POST',
          body: input ?? {},
          idempotencyKey,
        })
      ).data,
    onSuccess: (invoice) => {
      applyWrite(client, invoices, invoice);
      void client.invalidateQueries({ queryKey: [...invoices.keys.detail(id)] });
    },
  });
}

/* ── Credit notes ──────────────────────────────────────────────────── */

export const CREDIT_NOTE_REASONS = [
  { value: 'billing_error', label: 'Billing error' },
  { value: 'order_change', label: 'Order change' },
  { value: 'product_unsatisfactory', label: 'Product unsatisfactory' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'other', label: 'Other' },
] as const;

export const creditNoteSchema = z.object({
  id: zId,
  number: z.string(),
  invoice_id: zId,
  customer_id: zId,
  currency: z.string(),
  reason: z.string(),
  memo: z.string().nullable(),
  subtotal_minor: zMinor,
  tax_minor: zMinor,
  total_minor: zMinor,
  applied_to_invoice_minor: zMinor,
  credited_to_balance_minor: zMinor,
  issued_at: zInstant.nullable(),
  lines: z
    .array(
      z.object({
        id: zId,
        invoice_line_id: z.string().nullable(),
        description: z.string(),
        subtotal_minor: zMinor,
        tax_minor: zMinor,
        total_minor: zMinor,
        tax_snapshot: z.unknown().nullish(),
      }),
    )
    .optional(),
});
export type CreditNote = z.infer<typeof creditNoteSchema>;

export const creditNotes = defineResource('credit_notes', '/credit-notes', creditNoteSchema);

export function useCreditNotes(
  params: { invoice_id?: string; customer_id?: string } = {},
  options: { enabled?: boolean } = {},
) {
  return useResourceList(creditNotes, params, options);
}

export interface CreditNoteInput {
  reason: (typeof CREDIT_NOTE_REASONS)[number]['value'];
  memo?: string | null;
  /** Omit amount_minor to credit the whole line. */
  lines: { invoice_line_id: string; amount_minor?: number | null; description?: string | null }[];
}

export function useIssueCreditNote(invoiceId: string) {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (input: CreditNoteInput, idempotencyKey) =>
      (
        await apiRequest(itemEnvelope(creditNoteSchema), `/invoices/${invoiceId}/credit-notes`, {
          method: 'POST',
          body: input,
          idempotencyKey,
        })
      ).data,
    onSuccess: (note) => {
      applyWrite(client, creditNotes, note);
      void client.invalidateQueries({ queryKey: invoices.keys.all() });
    },
  });
}

/* ── Billable items ────────────────────────────────────────────────── */

export const billableItemSchema = z.object({
  id: zId,
  customer_id: zId,
  subscription_id: z.string().nullable(),
  contract_id: z.string().nullable(),
  price_id: z.string().nullable(),
  source_type: z.string(),
  source_id: z.string().nullable(),
  description: z.string(),
  currency: z.string(),
  quantity: zDecimal,
  unit_amount: zDecimal,
  /** Negotiated discount (0–100), applied before tax. */
  discount_percent: zDecimal.optional(),
  amount_minor: zMinor,
  proration: z.boolean(),
  service_period_start: zInstant.nullable(),
  service_period_end: zInstant.nullable(),
  tax_rate_ids: z.array(z.string()),
  tax_behavior: z.string().nullish(),
  bill_on: z.string().nullable(),
  status: z.string(),
  invoice_id: z.string().nullable(),
  calculation_trace: z.unknown().nullish(),
  created_at: zInstant.nullable(),
});
export type BillableItem = z.infer<typeof billableItemSchema>;

export const billableItems = defineResource('billable_items', '/billable-items', billableItemSchema);

/**
 * NOTE: GET /billable-items validates `status[]` against invoice statuses, so
 * `pending` cannot be filtered server-side yet; filter rows client-side.
 */
export function useBillableItems(
  params: { customer_id?: string; subscription_id?: string } = {},
  options: { enabled?: boolean } = {},
) {
  return useResourceList(billableItems, params, options);
}

export interface BillableItemInput {
  customer_id: string;
  description: string;
  currency: string;
  quantity?: string;
  unit_amount?: string | null;
  price_id?: string | null;
  discount_percent?: string | null;
  tax_rate_ids?: string[];
  tax_behavior?: 'exclusive' | 'inclusive';
  bill_on?: string | null;
}

export function useCreateBillableItem() {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (input: BillableItemInput, idempotencyKey) =>
      (
        await apiRequest(itemEnvelope(billableItemSchema), '/billable-items', {
          method: 'POST',
          body: input,
          idempotencyKey,
        })
      ).data,
    onSuccess: (item) => applyWrite(client, billableItems, item),
  });
}

export function useCancelBillableItem() {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (id: string, idempotencyKey) =>
      (
        await apiRequest(itemEnvelope(billableItemSchema), `/billable-items/${id}/cancel`, {
          method: 'POST',
          idempotencyKey,
        })
      ).data,
    onSuccess: (item) => applyWrite(client, billableItems, item),
  });
}
